import type { StorefrontExtension, ThemeBundle } from './types';

/**
 * Registers every theme contributed by {@code extensions} through
 * {@code register}, and returns the registered theme ids in order.
 *
 * <p>All entries are validated before any is registered, so a bad extension
 * leaves the registry untouched. It throws when a theme id is empty, collides
 * with one of {@code builtInIds}, or is contributed twice. Replacing a
 * built-in theme silently would change what every tenant on that theme sees,
 * so it is an error rather than an override.
 *
 * <p>Kept free of the runtime registry and of the `@storefront/extensions`
 * alias so it can be unit-tested without the Next build.
 */
export function registerExtensionThemes(
  extensions: readonly StorefrontExtension[],
  register: (themeId: string, bundle: ThemeBundle) => unknown,
  builtInIds: Iterable<string>,
): string[] {
  const builtIn = new Set(builtInIds);
  const owners = new Map<string, string>();
  const entries: Array<{ themeId: string; bundle: ThemeBundle }> = [];

  for (const extension of extensions) {
    for (const entry of extension.themes ?? []) {
      const { themeId } = entry;
      if (typeof themeId !== 'string' || themeId.trim() === '') {
        throw new Error(
          `Storefront extension "${extension.id}" contributes a theme with an empty themeId.`,
        );
      }
      if (builtIn.has(themeId)) {
        throw new Error(
          `Storefront extension "${extension.id}" contributes theme "${themeId}", ` +
            `which collides with a built-in theme. Choose a different themeId.`,
        );
      }
      const previousOwner = owners.get(themeId);
      if (previousOwner !== undefined) {
        throw new Error(
          `Storefront extension "${extension.id}" contributes theme "${themeId}", ` +
            `which extension "${previousOwner}" already contributes.`,
        );
      }
      owners.set(themeId, extension.id);
      entries.push(entry);
    }
  }

  for (const { themeId, bundle } of entries) {
    register(themeId, bundle);
  }
  return entries.map((entry) => entry.themeId);
}

const API_PREFIX = /^[a-z][a-z0-9-]*$/;

export interface ExtensionApiRegistry {
  readonly prefixes: readonly string[];
  has(prefix: string): boolean;
}

/** Builds the immutable API-prefix view from the extension module loaded at startup. */
export function extensionApiPrefixes(
  extensions: readonly StorefrontExtension[],
): ExtensionApiRegistry {
  const values = new Set<string>();
  for (const extension of extensions) {
    for (const prefix of extension.apiPrefixes ?? []) {
      if (!API_PREFIX.test(prefix)) {
        throw new Error(`Storefront extension "${extension.id}" has invalid API prefix "${prefix}".`);
      }
      values.add(prefix);
    }
  }

  const prefixes = Object.freeze([...values].sort());
  return {
    prefixes,
    has(prefix: string): boolean {
      return values.has(prefix);
    },
  };
}
